import axios from 'axios';
import toast from 'react-hot-toast';

const API_URL = process.env.REACT_APP_API_URL || '';

const api = axios.create({
  baseURL: `${API_URL}/api`,
  headers: { 'Content-Type': 'application/json' },
  timeout: 30000,
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  const wsId = localStorage.getItem('workspaceId');
  if (wsId) config.headers['x-workspace-id'] = wsId;
  return config;
});

api.interceptors.response.use(
  (response) => response,
  (error) => {
    const { response } = error;
    if (response?.status === 401 && !window.location.pathname.includes('/login')) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    if (response?.status === 402) {
      const msg = response.data?.message || 'Subscription expired';
      toast.error(msg, { duration: 6000 });
      if (response.data?.suspended) {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        setTimeout(() => { window.location.href = '/login'; }, 3000);
      }
      // planExpired: intentionally do NOT log out — the PrivateRoute plan-expiry
      // gate steers the user to the renewal screen instead of a forced logout.
    }
    return Promise.reject(error);
  }
);

// ─── AUTH ─────────────────────────────────────────────────────────────────
export const authAPI = {
  login: (data) => api.post('/auth/login', data),
  googleLogin: (credential) => api.post('/auth/google', { credential }),
  getMe: () => api.get('/auth/me'),
  changePassword: (data) => api.put('/auth/change-password', data),
  forgotPassword: (data) => api.post('/auth/forgot-password', data),
  resetPassword: (data) => api.post('/auth/reset-password', data),
  updateProfile: (data) => api.put('/auth/profile', data),
  // Owner deletes the whole organization: request (emails a code) -> confirm (code) -> 14-day grace -> cancel any time.
  requestOrgDeletion: () => api.post('/auth/delete-organization/request'),
  confirmOrgDeletion: (data) => api.post('/auth/delete-organization/confirm', data),
  cancelOrgDeletion: () => api.post('/auth/delete-organization/cancel'),
};

// ─── PUBLIC (no auth) ─────────────────────────────────────────────────────
export const publicAPI = {
  // Account deletion from the public page: email -> emailed link -> confirm; the cancel link is emailed too.
  deleteAccountRequest: (data) => api.post('/public/delete-account/request', data),
  deleteAccountConfirm: (token) => api.post('/public/delete-account/confirm', { token }),
  deleteAccountCancel: (token) => api.post('/public/delete-account/cancel', { token }),
  getPlans: () => api.get('/public/plans'),
  getPaymentsStatus: () => api.get('/public/payments-status'),
  getAffiliate: (slug) => api.get(`/public/affiliate/${slug}`),
  checkReferralCode: (code) => api.get(`/public/referral-code/${encodeURIComponent(code)}`),
  orgSignup: (data) => api.post('/public/org-signup', data),
  selfSignupStart: (data) => api.post('/public/self-signup/start', data),
  selfSignupVerify: (data) => api.post('/public/self-signup/verify', data),
  getGoogleConfig: () => api.get('/public/google-config'),
  googleSignupStart: (credential) => api.post('/public/google-signup/start', { credential }),
  googleSignupComplete: (data) => api.post('/public/google-signup/complete', data),
  affiliateSignup: (data) => api.post('/public/affiliate-signup', data),
};

// ─── SUPER ADMIN ──────────────────────────────────────────────────────────
export const superAdminAPI = {
  getPlanInvoices: (params) => api.get('/superadmin/plan-invoices', { params }),
  exportPlanInvoices: (params) => api.get('/superadmin/plan-invoices/export', { params, responseType: 'blob' }),
  downloadPlanInvoicePdf: (id) => api.get(`/superadmin/plan-invoices/${id}/pdf`, { responseType: 'blob' }).then((r) => ({ blob: r.data, filename: r.headers['content-disposition']?.match(/filename="?([^"]+)"?/)?.[1] || null })),
  getInvoiceSettings: () => api.get('/superadmin/invoice-settings'),
  updateInvoiceSettings: (data) => api.put('/superadmin/invoice-settings', data),
  getDashboard: () => api.get('/superadmin/dashboard'),
  getUsageAnalytics: (params) => api.get('/superadmin/usage-analytics', { params }),
  getOrganizations: (params) => api.get('/superadmin/organizations', { params }),
  getOrganization: (id) => api.get(`/superadmin/organizations/${id}`),
  createOrganization: (data) => api.post('/superadmin/organizations', data),
  updateOrganization: (id, data) => api.put(`/superadmin/organizations/${id}`, data),
  suspendOrganization: (id, data) => api.post(`/superadmin/organizations/${id}/suspend`, data),
  unsuspendOrganization: (id) => api.post(`/superadmin/organizations/${id}/unsuspend`),
  deleteOrganization: (id) => api.delete(`/superadmin/organizations/${id}`),
  getPlans: () => api.get('/superadmin/plans'),
  updatePlan: (id, data) => api.put(`/superadmin/plans/${id}`, data),
  getCoupons: () => api.get('/superadmin/coupons'),
  createCoupon: (data) => api.post('/superadmin/coupons', data),
  updateCoupon: (id, data) => api.put(`/superadmin/coupons/${id}`, data),
  deleteCoupon: (id) => api.delete(`/superadmin/coupons/${id}`),
  getAffiliateProgramSettings: () => api.get('/superadmin/affiliates/program-settings'),
  updateAffiliateProgramSettings: (data) => api.put('/superadmin/affiliates/program-settings', data),
  initiateAffiliateClosure: () => api.post('/superadmin/affiliates/program-settings/initiate-closure'),
  getAffiliateClosureStatus: () => api.get('/superadmin/affiliates/program-settings/closure-status'),
  getAffiliates: (params) => api.get('/superadmin/affiliates', { params }),
  getAffiliate: (id) => api.get(`/superadmin/affiliates/${id}`),
  createAffiliate: (data) => api.post('/superadmin/affiliates', data),
  updateAffiliate: (id, data) => api.put(`/superadmin/affiliates/${id}`, data),
  deleteAffiliate: (id) => api.delete(`/superadmin/affiliates/${id}`),
  getAffiliateCommissions: (params) => api.get('/superadmin/affiliates/commissions', { params }),
  getPayoutBatches: (params) => api.get('/superadmin/affiliates/payout-batches', { params }),
  createPayoutBatch: (data) => api.post('/superadmin/affiliates/payout-batches', data),
  markPayoutBatchPaid: (id, data) => api.post(`/superadmin/affiliates/payout-batches/${id}/mark-paid`, data),
  cancelPayoutBatch: (id) => api.post(`/superadmin/affiliates/payout-batches/${id}/cancel`),
  getRewardTiers: (affiliateId) => api.get('/superadmin/affiliates/reward-tiers', { params: affiliateId ? { affiliateId } : {} }),
  createRewardTier: (data) => api.post('/superadmin/affiliates/reward-tiers', data),
  updateRewardTier: (id, data) => api.put(`/superadmin/affiliates/reward-tiers/${id}`, data),
  deleteRewardTier: (id) => api.delete(`/superadmin/affiliates/reward-tiers/${id}`),
  getAffiliateRewards: (id) => api.get(`/superadmin/affiliates/${id}/rewards`),
  // PAN card lives in the private store behind auth: fetched as a blob and opened locally
  getAffiliatePanDetails: (id) => api.get(`/superadmin/affiliates/${id}/pan-details`),
  verifyAffiliatePan: (id) => api.post(`/superadmin/affiliates/${id}/pan/verify`),
  rejectAffiliatePan: (id, data) => api.post(`/superadmin/affiliates/${id}/pan/reject`, data),
  downloadTdsReport: (financialYear) => api.get('/superadmin/affiliates/payout-batches/tds-report', { params: { financialYear }, responseType: 'blob' }),
  previewPayoutBatch: (params) => api.get('/superadmin/affiliates/payout-batches/preview', { params }),
  getAffiliatePanFile: (id) => api.get(`/superadmin/affiliates/${id}/pan-file`, { responseType: 'blob' }),
  markRewardFulfilled: (id, data) => api.post(`/superadmin/affiliates/reward-awards/${id}/mark-fulfilled`, data),
};

// ─── ORGANIZATIONS ────────────────────────────────────────────────────────
export const orgAPI = {
  getDashboard: (params) => api.get('/organizations/dashboard', { params }),
  getWorkspaces: () => api.get('/organizations/workspaces'),
  createWorkspace: (data) => api.post('/organizations/workspaces', data),
  getWorkspace: (id) => api.get(`/organizations/workspaces/${id}`),
  updateWorkspace: (id, data) => api.put(`/organizations/workspaces/${id}`, data),
  deleteWorkspace: (id) => api.delete(`/organizations/workspaces/${id}`),
  getReports: (params) => api.get('/organizations/reports', { params }),
  getSettings: () => api.get('/organizations/settings'),
  updateSettings: (data) => api.put('/organizations/settings', data),
  getWebhookWorkspaces: () => api.get('/organizations/webhook-workspaces'),
  getWebhookRoutes: () => api.get('/organizations/webhook-routes'),
  createWebhookRoute: (data) => api.post('/organizations/webhook-routes', data),
  updateWebhookRoute: (id, data) => api.put(`/organizations/webhook-routes/${id}`, data),
  deleteWebhookRoute: (id) => api.delete(`/organizations/webhook-routes/${id}`),
  removeUserFromWorkspace: (workspaceId, userId) => api.delete(`/organizations/workspaces/${workspaceId}/users/${userId}`),
};

// ─── BILLING ──────────────────────────────────────────────────────────────
export const billingAPI = {
  getSubscription: () => api.get('/billing/subscription'),
  createSubscription: (data) => api.post('/billing/subscription', data),
  getQuote: (data) => api.post('/billing/quote', data),
  createOrder: (data) => api.post('/billing/order', data),
  verifyOrder: (data) => api.post('/billing/order/verify', data),
  setAutoRenew: (autoRenew) => api.post('/billing/subscription/auto-renew', { autoRenew }),
  syncSubscription: () => api.post('/billing/subscription/sync'),
  getPayments: () => api.get('/billing/payments'),
  // GST tax invoice / credit note PDFs for plan purchases
  downloadPaymentInvoice: (paymentId) => api.get(`/billing/payments/${paymentId}/invoice`, { responseType: 'blob' }).then((r) => ({ blob: r.data, filename: r.headers['content-disposition']?.match(/filename="?([^"]+)"?/)?.[1] || null })),
  downloadCreditNote: (id) => api.get(`/billing/credit-notes/${id}/pdf`, { responseType: 'blob' }).then((r) => ({ blob: r.data, filename: r.headers['content-disposition']?.match(/filename="?([^"]+)"?/)?.[1] || null })),
  emailPlanDocument: (id) => api.post(`/billing/invoices/${id}/email`),
  getBillingDetails: () => api.get('/billing/billing-details'),
  updateBillingDetails: (data) => api.put('/billing/billing-details', data),
};

// ─── WORKSPACE ────────────────────────────────────────────────────────────
export const workspaceAPI = {
  get: () => api.get('/workspace'),
  update: (data) => api.put('/workspace', data),
};

// ─── LEADS ────────────────────────────────────────────────────────────────
export const leadsAPI = {
  getAll: (params) => api.get('/leads', { params }),
  get: (id) => api.get(`/leads/${id}`),
  create: (data) => api.post('/leads', data),
  update: (id, data) => api.put(`/leads/${id}`, data),
  delete: (id) => api.delete(`/leads/${id}`),
  getPipeline: (params) => api.get('/leads/pipeline', { params }),
  getMeta: (params) => api.get('/leads/meta', { params }),
  clientLookup: (params) => api.get('/leads/client-lookup', { params }),
  addNote: (id, note) => api.post(`/leads/${id}/note`, { note }),
  bulkAssign: (leadIds, assignedTo) => api.put('/leads/bulk-assign', { leadIds, assignedTo }),
  bulkUnassign: (leadIds) => api.put('/leads/bulk-unassign', { leadIds }),
  bulkAssignWorkspace: (leadIds, workspaceId) => api.put('/leads/bulk-assign-workspace', { leadIds, workspaceId }),
  bulkDelete: (leadIds) => api.delete('/leads/bulk-delete', { data: { leadIds } }),
  getBin: (params) => api.get('/leads/bin', { params }),
  restore: (id) => api.put(`/leads/bin/${id}/restore`),
  bulkRestore: (leadIds) => api.put('/leads/bin/restore', { leadIds }),
  permanentlyDelete: (id) => api.delete(`/leads/bin/${id}`),
  bulkPermanentlyDelete: (leadIds) => api.delete('/leads/bin', { data: { leadIds } }),
  getMyPrivacyStatus: () => api.get('/leads/privacy/status'),
  requestPrivacy: () => api.post('/leads/privacy/request'),
  disablePrivacy: () => api.put('/leads/privacy/disable'),
  getPrivacyRequests: (params) => api.get('/leads/privacy-requests', { params }),
  approvePrivacyRequest: (id) => api.put(`/leads/privacy-requests/${id}/approve`),
  rejectPrivacyRequest: (id) => api.put(`/leads/privacy-requests/${id}/reject`),
  importCSV: (file, workspaceId, onProgress) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/leads/import/csv', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      params: workspaceId ? { workspaceId } : undefined,
      onUploadProgress: onProgress,
    });
  },
};

// ─── FOLLOWUPS ────────────────────────────────────────────────────────────
export const followupsAPI = {
  getAll: (params) => api.get('/followups', { params }),
  getOverdueCount: () => api.get('/followups/overdue-count'),
  create: (data) => api.post('/followups', data),
  update: (id, data) => api.put(`/followups/${id}`, data),
  complete: (id, data) => api.post(`/followups/${id}/complete`, data),
  cancel: (id) => api.post(`/followups/${id}/cancel`),
};

// ─── APPOINTMENTS ─────────────────────────────────────────────────────────
export const appointmentsAPI = {
  getAll: (params) => api.get('/appointments', { params }),
  getCalendar: (params) => api.get('/appointments/calendar', { params }),
  getToday: () => api.get('/appointments/today'),
  getUpcoming: () => api.get('/appointments/upcoming'),
  get: (id) => api.get(`/appointments/${id}`),
  create: (data) => api.post('/appointments', data),
  update: (id, data) => api.put(`/appointments/${id}`, data),
  updateStatus: (id, data) => api.put(`/appointments/${id}/status`, data),
  delete: (id) => api.delete(`/appointments/${id}`),
};

// ─── QUOTATIONS ───────────────────────────────────────────────────────────
export const quotationsAPI = {
  getAll: (params) => api.get('/quotations', { params }),
  get: (id) => api.get(`/quotations/${id}`),
  create: (data) => api.post('/quotations', data),
  update: (id, data) => api.put(`/quotations/${id}`, data),
  updateStatus: (id, data) => api.put(`/quotations/${id}/status`, data),
  downloadPDF: (id) => api.get(`/quotations/${id}/pdf`, { responseType: 'blob' }).then(r => ({ blob: r.data, filename: (r.headers['content-disposition']?.match(/filename="?([^"]+)"?/)?.[1]) || null })),
  sendEmail: (id) => api.post(`/quotations/${id}/send-email`),
  whatsappShare: (id) => api.post(`/quotations/${id}/whatsapp-share`),
  delete: (id) => api.delete(`/quotations/${id}`),
};

// ─── INVOICES ─────────────────────────────────────────────────────────────
export const invoicesAPI = {
  getAll: (params) => api.get('/invoices', { params }),
  get: (id) => api.get(`/invoices/${id}`),
  getPendingByClient: (params) => api.get('/invoices/pending-by-client', { params }),
  create: (data) => api.post('/invoices', data),
  update: (id, data) => api.put(`/invoices/${id}`, data),
  downloadPDF: (id) => api.get(`/invoices/${id}/pdf`, { responseType: 'blob' }).then(r => ({ blob: r.data, filename: (r.headers['content-disposition']?.match(/filename="?([^"]+)"?/)?.[1]) || null })),
  sendEmail: (id) => api.post(`/invoices/${id}/send-email`),
  whatsappShare: (id) => api.post(`/invoices/${id}/whatsapp-share`),
  markSent: (id) => api.put(`/invoices/${id}/mark-sent`),
  delete: (id) => api.delete(`/invoices/${id}`),
};

// ─── PAYMENTS ─────────────────────────────────────────────────────────────
export const paymentsAPI = {
  getAll: (params) => api.get('/payments', { params }),
  getStats: (params) => api.get('/payments/stats', { params }),
  add: (data) => api.post('/payments', data),
};

// ─── CONTENT ──────────────────────────────────────────────────────────────
export const contentAPI = {
  getAll: (params) => api.get('/content', { params }),
  getCalendar: (params) => api.get('/content/calendar', { params }),
  getPipeline: (params) => api.get('/content/pipeline', { params }),
  getArchived: (params) => api.get('/content/archived', { params }),
  get: (id) => api.get(`/content/${id}`),
  getHistory: (id) => api.get(`/content/${id}/history`),
  create: (data) => api.post('/content', data),
  update: (id, data) => api.put(`/content/${id}`, data),
  delete: (id) => api.delete(`/content/${id}`),
  archive: (id) => api.post(`/content/${id}/archive`),
  unarchive: (id) => api.post(`/content/${id}/unarchive`),
  archiveBulk: (params) => api.post('/content/archive-bulk', {}, { params }),
};

// ─── EXPENSES ─────────────────────────────────────────────────────────────
export const expensesAPI = {
  getAll: (params) => api.get('/expenses', { params }),
  getSummary: (params) => api.get('/expenses/summary', { params }),
  getRemovedSubmitters: (params) => api.get('/expenses/removed-submitters', { params }),
  create: (data) => api.post('/expenses', data),
  update: (id, data) => api.put(`/expenses/${id}`, data),
  approve: (id) => api.post(`/expenses/${id}/approve`),
  reject: (id, reason) => api.post(`/expenses/${id}/reject`, { reason }),
  delete: (id) => api.delete(`/expenses/${id}`),
};

// ─── REPORTS ──────────────────────────────────────────────────────────────
export const reportsAPI = {
  getDashboard: (params) => api.get('/reports/dashboard', { params }),
  getLoginSummary: () => api.get('/reports/login-summary'),
  getAdvanced: (params) => api.get('/reports/advanced', { params }),
};

// ─── TEAM ACTIVITY ────────────────────────────────────────────────────────
export const teamActivityAPI = {
  getSummary: (params) => api.get('/team-activity/summary', { params }),
  getFeed: (params) => api.get('/team-activity/feed', { params }),
  getEmployeeStats: (userId, params) => api.get(`/team-activity/employee-stats/${userId}`, { params }),
};

// ─── NOTIFICATIONS ────────────────────────────────────────────────────────
export const notificationsAPI = {
  getAll: (params) => api.get('/notifications', { params }),
  getCount: () => api.get('/notifications/count'),
  getRecent: () => api.get('/notifications/recent'),
  markRead: (id) => api.put(`/notifications/${id}/read`),
  markAllRead: () => api.put('/notifications/mark-all-read'),
};

// ─── USERS ────────────────────────────────────────────────────────────────
export const usersAPI = {
  getAll: (params) => api.get('/users', { params }),
  get: (id) => api.get(`/users/${id}`),
  create: (data) => api.post('/users', data),
  update: (id, data) => api.put(`/users/${id}`, data),
  delete: (id) => api.delete(`/users/${id}`),
};

// ─── EMPLOYEE LABELS ──────────────────────────────────────────────────────
export const employeeLabelsAPI = {
  getAll: () => api.get('/employee-labels'),
  create: (data) => api.post('/employee-labels', data),
  delete: (id) => api.delete(`/employee-labels/${id}`),
};

// ─── META INTEGRATIONS ────────────────────────────────────────────────────
export const metaIntegrationAPI = {
  getAll: () => api.get('/meta-integrations'),
  connect: (data) => api.post('/meta-integrations', data),
  update: (id, data) => api.put(`/meta-integrations/${id}`, data),
  disconnect: (id) => api.delete(`/meta-integrations/${id}`),
  sync: (id) => api.post(`/meta-integrations/${id}/sync`),
  getForms: (id) => api.get(`/meta-integrations/${id}/forms`),
  updateFormRoutes: (id, formRoutes) => api.put(`/meta-integrations/${id}/form-routes`, { formRoutes }),
  testConnection: (id) => api.get(`/meta-integrations/${id}/test`),
};

// ─── META CONVERSIONS API ─────────────────────────────────────────────────
export const metaCapiAPI = {
  getAll: () => api.get('/meta-capi'),
  connect: (data) => api.post('/meta-capi', data),
  update: (id, data) => api.put(`/meta-capi/${id}`, data),
  disconnect: (id) => api.delete(`/meta-capi/${id}`),
};

// ─── GOOGLE ADS OFFLINE CONVERSIONS ───────────────────────────────────────
export const googleAdsCapiAPI = {
  getAll: () => api.get('/google-ads-capi'),
  oauthStart: (workspaceId) => api.post('/google-ads-capi/oauth/start', { workspaceId }),
  finishConnect: (data) => api.post('/google-ads-capi/finish-connect', data),
  update: (id, data) => api.put(`/google-ads-capi/${id}`, data),
  disconnect: (id) => api.delete(`/google-ads-capi/${id}`),
};

// ─── WASEND WHATSAPP INTEGRATION ──────────────────────────────────────────
export const whatsappIntegrationAPI = {
  getAll: () => api.get('/whatsapp-integration'),
  oauthStart: (workspaceId) => api.post('/whatsapp-integration/oauth/start', { workspaceId }),
  disconnect: (id) => api.delete(`/whatsapp-integration/${id}`),
  sendMessage: (data) => api.post('/whatsapp-integration/send-message', data),
  bulkSend: (data) => api.post('/whatsapp-integration/bulk-send', data),
  getBulkSends: () => api.get('/whatsapp-integration/bulk-sends'),
  // Approved templates usable for every one of these leads (intersection across their workspaces)
  getTemplates: (leadIds) => api.get('/whatsapp-integration/templates', { params: { leadIds: leadIds.join(',') } }),
  // Whether free text can currently reach this lead (they messaged the business in the last 24h)
  getWindow: (leadId) => api.get(`/whatsapp-integration/window/${leadId}`),
};

// ─── COMMUNICATION ────────────────────────────────────────────────────────
export const communicationAPI = {
  logCall: (data) => api.post('/communication/calls', data),
  getCalls: (params) => api.get('/communication/calls', { params }),
  logWhatsApp: (data) => api.post('/communication/whatsapp', data),
  sendEmail: (data) => api.post('/communication/email', data),
};

export default api;
